import { tx as translateInterfaceText } from '@/utils/i18n';
import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogOverlay } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PaymentMethodModal } from '@/components/finance/PaymentMethodModal';
import { CardBrandIcon } from '@/components/finance/CardBrandIcon';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { subscriptionService, type SurplusAttempt } from '@/services/subscriptionService';
import { type AutoRechargeConfig, invoiceService } from '@/services/invoiceService';
import { SurplusQuantitySelector } from './SurplusQuantitySelector';
import { SurplusPricingSummary } from './SurplusPricingSummary';
import { formatSurplusCurrency, getSurplusQuote, isSurplusAttempt, isValidSurplusQuantity, MAX_SURPLUS_PLAYS, MIN_SURPLUS_PLAYS } from '@/utils/surplusPricing';

interface PlaysManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PlaysManagementModal: React.FC<PlaysManagementModalProps> = ({
  open,
  onOpenChange
}) => {
  useLanguage();
  const { user, refreshUser } = useCustomAuth();
  const { paymentMethods, isLoading: isLoadingCards, selectedCard, setSelectedCard, fetchCards, deleteCard } = usePaymentMethods(user?.id_Stripe_Client, !open);

  const [view, setView] = useState<'comprar' | 'recarga'>('comprar');
  const [amount, setAmount] = useState(2000);
  const [scheduleStatus, setScheduleStatus] = useState<'idle' | 'scheduling' | 'scheduled'>('idle');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [rechargeConfig, setRechargeConfig] = useState<AutoRechargeConfig | null>(null);
  const [isLoadingRecharge, setIsLoadingRecharge] = useState(false);
  const [isCancellingRecharge, setIsCancellingRecharge] = useState(false);
  const [isResumingRecharge, setIsResumingRecharge] = useState(false);
  const [hasRecoveredPending, setHasRecoveredPending] = useState(false);
  const [subscriptionPending, setSubscriptionPending] = useState(false);
  const [purchasePending, setPurchasePending] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [rechargeError, setRechargeError] = useState<string | null>(null);
  const purchaseAttempt = useRef<SurplusAttempt | null>(null);
  const hydratedRechargeCard = useRef(false);
  const savingRecharge = useRef(false);
  const rechargeRevision = useRef(0);
  const rechargeReadSequence = useRef(0);
  const hasLoadedRecharge = useRef(false);
  const attemptStorageKey = `hostvsl:surplus-purchase:${user?.id_Stripe_Client || ''}`;

  const clearPurchaseAttempt = () => {
    purchaseAttempt.current = null;
    sessionStorage.removeItem(attemptStorageKey);
  };

  useEffect(() => {
    if (!open || !user?.id_Stripe_Client) return;
    let active = true;
    hydratedRechargeCard.current = false;
    setIsLoadingRecharge(true);
    setHasRecoveredPending(false);
    setSubscriptionPending(false);
    setRechargeConfig(null);
    hasLoadedRecharge.current = false;
    setRechargeError(null);
    try {
      const pending = JSON.parse(sessionStorage.getItem(attemptStorageKey) || 'null');
      if (isSurplusAttempt(pending)) {
        purchaseAttempt.current = pending;
        setAmount(pending.quantity);
        setView('comprar');
        setPurchasePending(true);
        setPurchaseError('Há uma compra aguardando confirmação. Consulte esta tentativa antes de iniciar outra compra.');
      } else {
        purchaseAttempt.current = null;
        setPurchasePending(false);
      }
    } catch {
      purchaseAttempt.current = null;
      setPurchasePending(false);
    }
    const loadRecharge = async (initial = false) => {
      if (savingRecharge.current) return;
      const revision = rechargeRevision.current;
      const sequence = ++rechargeReadSequence.current;
      const [response, recovered] = await Promise.all([
        invoiceService.getAutoRecharge(user.id_Stripe_Client),
        subscriptionService.recoverPendingCheckout(user.id_Stripe_Client),
      ]);
      if (!active || savingRecharge.current || revision !== rechargeRevision.current || sequence !== rechargeReadSequence.current) return;
      setHasRecoveredPending(recovered?.success === true && !!recovered.data);
      setSubscriptionPending(recovered?.data?.kind === 'subscription');
      if (recovered?.success !== true || !recovered.data) setPurchaseError(recovered?.message || 'Não foi possível consultar pagamentos pendentes. Reabra o modal para tentar novamente.');
      else if (recovered.data.kind === 'subscription') setPurchaseError('Há uma assinatura aguardando confirmação. Consulte esse pagamento no checkout antes de comprar plays extras.');
      else if (recovered.data.kind === 'surplus' && recovered.data.surplus) {
        purchaseAttempt.current = recovered.data.surplus;
        setAmount(recovered.data.surplus.quantity);
        setView('comprar');
        setPurchasePending(true);
        setPurchaseError('Há uma compra aguardando confirmação. Consulte esta tentativa antes de iniciar outra compra.');
      }
      if (response.success && response.data) {
        hasLoadedRecharge.current = true;
        setRechargeConfig(response.data);
        setScheduleStatus(response.data.enabled ? 'scheduled' : 'idle');
        setRechargeError(response.data.last_error);
      } else if (initial || !hasLoadedRecharge.current) {
        setRechargeError(response.message || 'Não foi possível carregar a recarga automática. Reabra o modal para tentar novamente.');
      }
      setIsLoadingRecharge(false);
    };
    void loadRecharge(true);
    const refresh = () => { void loadRecharge(); };
    const interval = window.setInterval(refresh, 10000);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
    };
  }, [open, user?.id_Stripe_Client, attemptStorageKey]);

  useEffect(() => {
    if (!purchasePending || isLoadingCards || !purchaseAttempt.current) return;
    setSelectedCard(paymentMethods.find(card => card.id_stripe === purchaseAttempt.current?.card) || null);
  }, [purchasePending, isLoadingCards, paymentMethods, setSelectedCard]);

  useEffect(() => {
    if (view !== 'recarga' || !rechargeConfig || hydratedRechargeCard.current || isLoadingCards || purchasePending) return;
    setAmount(rechargeConfig.quantity);
    const savedCard = paymentMethods.find(card => card.id_stripe === rechargeConfig.id_Stripe_Card);
    if (savedCard) setSelectedCard(savedCard);
    if (rechargeConfig.enabled && !savedCard) setSelectedCard(null);
    hydratedRechargeCard.current = true;
  }, [view, rechargeConfig, paymentMethods, isLoadingCards, purchasePending, setSelectedCard]);


  const STEP = 1000;
  const MIN = MIN_SURPLUS_PLAYS;
  const MAX = MAX_SURPLUS_PLAYS;
  const quantityValid = isValidSurplusQuantity(amount);

  const handleIncrement = () => {
    if (!purchasePending && !isPurchasing) setAmount(prev => Math.max(MIN, Math.min(Number.isFinite(prev) ? prev + STEP : MIN, MAX)));
  };

  const handleDecrement = () => {
    if (!purchasePending && !isPurchasing) setAmount(prev => Math.min(MAX, Math.max(Number.isFinite(prev) ? prev - STEP : MIN, MIN)));
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString(getInterfaceLanguage());
  };

  const formatCurrency = formatSurplusCurrency;

  const handleConfirmPurchase = async () => {
    if (!user?.id_Stripe_Client || isPurchasing) return;
    if (!purchasePending && !quantityValid) {
      toast.error(translateInterfaceText("Informe uma quantidade inteira a partir de 1.000 plays.")); return;
    }
    if (isLoadingRecharge || !hasRecoveredPending || !rechargeConfig || rechargeConfig.processing || subscriptionPending) {
      toast.error(translateInterfaceText("Consulte o pagamento em processamento antes de iniciar outra compra."));
      return;
    }
    setIsPurchasing(true);
    setPurchaseError(null);
    try {
      // Consultar novamente no clique também cobre operações iniciadas em outra aba.
      const recovered = await subscriptionService.recoverPendingCheckout(user.id_Stripe_Client);
      if (recovered.success !== true || !recovered.data) {
        const message = recovered.message || 'Não foi possível consultar pagamentos pendentes. Tente novamente antes de pagar.';
        setPurchaseError(message); toast.error(message); return;
      }
      if (recovered.data.kind === 'subscription') {
        setSubscriptionPending(true);
        const message = 'Há uma assinatura aguardando confirmação. Consulte esse pagamento no checkout antes de comprar plays extras.';
        setPurchaseError(message); toast.error(message); return;
      }
      if (recovered.data.kind === 'surplus' && recovered.data.surplus) {
        purchaseAttempt.current = recovered.data.surplus;
        setAmount(recovered.data.surplus.quantity);
        setPurchasePending(true);
      }
      if (!selectedCard && !purchaseAttempt.current) {
        toast.error(translateInterfaceText("Cadastre um cartão antes de comprar")); return;
      }
      if (!purchaseAttempt.current && selectedCard) {
        const quote = getSurplusQuote(amount);
        const attempt = { id: crypto.randomUUID(), quantity: amount, card: selectedCard.id_stripe,
          total_cents: quote.total_cents, unit_amount_decimal: quote.unit_amount_decimal };
        sessionStorage.setItem(attemptStorageKey, JSON.stringify(attempt));
        purchaseAttempt.current = attempt;
      }
      if (!purchaseAttempt.current) return;
      const attempt = purchaseAttempt.current;
      const response = await invoiceService.createInvoiceAndPay(user.id_Stripe_Client, {
        quantity: attempt.quantity, id_Stripe_Card: attempt.card, request_id: attempt.id,
      });
      if (response?.success !== true || response.data?.credited !== true) {
        const message = response.message || 'Não foi possível confirmar o pagamento e a entrega dos plays. Tente consultar esta mesma compra novamente.';
        setPurchaseError(message);
        setPurchasePending(response.retryable !== false);
        if (response.retryable === false) clearPurchaseAttempt();
        toast.error(message);
        return;
      }
      clearPurchaseAttempt();
      setPurchasePending(false);
      toast.success(translateInterfaceText("{value0} plays adicionados com sucesso!", { value0: (formatNumber(response.data.quantity)) }));
      await refreshUser().catch(() => {});
      onOpenChange(false);
    } catch {
      setPurchasePending(!!purchaseAttempt.current);
      const message = 'Não foi possível confirmar a compra. Tente novamente para consultar a mesma tentativa.';
      setPurchaseError(message);
      toast.error(message);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleScheduleRecharge = async () => {
    if (!quantityValid) { toast.error(translateInterfaceText("Informe uma quantidade inteira a partir de 1.000 plays.")); return; }
    if (!user?.id_Stripe_Client || !selectedCard || !rechargeConfig) {
      toast.error(translateInterfaceText("Selecione um cartão e aguarde o carregamento da configuração."));
      return;
    }
    rechargeRevision.current++;
    savingRecharge.current = true;
    setScheduleStatus('scheduling');
    const response = await invoiceService.setAutoRecharge(user.id_Stripe_Client, {
      buyAutomatic: true, quantity: amount, id_Stripe_Card: selectedCard.id_stripe,
    });
    savingRecharge.current = false;
    if (response.success && response.data) {
      setRechargeConfig(response.data);
      setRechargeError(null);
      setScheduleStatus('scheduled');
      toast.success(translateInterfaceText("Recarga automática de {value0} plays ativada!", { value0: (formatNumber(amount)) }));
    } else {
      setScheduleStatus('idle');
      setRechargeError(response.message || 'Não foi possível ativar a recarga automática.');
      toast.error(response.message || translateInterfaceText("Não foi possível ativar a recarga automática."));
    }
  };

  const handleCancelSchedule = async () => {
    if (!user?.id_Stripe_Client) return;
    rechargeRevision.current++;
    savingRecharge.current = true;
    setIsCancellingRecharge(true);
    const response = await invoiceService.setAutoRecharge(user.id_Stripe_Client, { buyAutomatic: false });
    savingRecharge.current = false;
    setIsCancellingRecharge(false);
    if (response.success && response.data) {
      setRechargeConfig(response.data);
      setScheduleStatus('idle');
      toast.info(response.data.processing ? translateInterfaceText("Recargas futuras canceladas. A recarga em processamento será concluída.") : translateInterfaceText("Recarga automática cancelada."));
    } else {
      toast.error(response.message || translateInterfaceText("Não foi possível cancelar a recarga automática."));
    }
  };

  const handleResumeRecharge = async () => {
    if (!user?.id_Stripe_Client) return;
    rechargeRevision.current++;
    savingRecharge.current = true;
    setIsResumingRecharge(true);
    const response = await invoiceService.resumeAutoRecharge(user.id_Stripe_Client);
    if (!response.success) toast.error(response.message || translateInterfaceText("Não foi possível confirmar a recarga automática."));
    const latest = response.success ? response : await invoiceService.getAutoRecharge(user.id_Stripe_Client);
    if (latest.success && latest.data) {
      setRechargeConfig(latest.data);
      setScheduleStatus(latest.data.enabled ? 'scheduled' : 'idle');
      setRechargeError(latest.data.last_error || (response.success ? null : response.message || null));
    }
    if (response.success) await refreshUser().catch(() => {});
    savingRecharge.current = false;
    setIsResumingRecharge(false);
  };

  const handleTabChange = (newView: 'comprar' | 'recarga') => {
    if (isPurchasing || purchasePending || scheduleStatus === 'scheduling' || isCancellingRecharge || isResumingRecharge) return;
    setView(newView);
    if (newView === 'recarga') {
      hydratedRechargeCard.current = false;
      setScheduleStatus(rechargeConfig?.enabled ? 'scheduled' : 'idle');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className="bg-black/50" />
      <DialogContent
        className="!w-[calc(100vw-32px)] sm:!w-[440px] p-0 overflow-y-auto max-h-[calc(100dvh-32px)] gap-0 border-0"
        style={{
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }}
        hideCloseButton
        onInteractOutside={(e) => {
          if (paymentModalOpen) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (paymentModalOpen) e.preventDefault();
        }}
      >
        {/* Header com Tabs - PRD 2.0: Azul escuro + texto branco */}
        <div className="p-2 bg-white">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleTabChange('comprar')}
              className={cn(
                "h-[44px] rounded-[10px] font-semibold text-sm transition-all duration-200",
                view === 'comprar'
                  ? "bg-[#2563EB] text-white"
                  : "bg-white text-[#6B7280] hover:bg-[#F3F4F6]"
              )}
            >
              {translateInterfaceText("Comprar")}</button>
            <button
              onClick={() => handleTabChange('recarga')}
              className={cn(
                "h-[44px] rounded-[10px] font-semibold text-sm transition-all duration-200",
                view === 'recarga'
                  ? "bg-[#2563EB] text-white"
                  : "bg-white text-[#6B7280] hover:bg-[#F3F4F6]"
              )}
            >
              {translateInterfaceText("Recarga Automática")}</button>
          </div>
        </div>

        {/* Conteúdo - PRD 2.0: pt-6 (24px) entre tabs e título */}
        <div className="px-7 pb-7 pt-6 bg-white">
          {view === 'comprar' && purchaseError && <p role="alert" className="text-sm text-red-700 mb-4">{purchaseError}</p>}
          {subscriptionPending && <a href="/checkout" className="mb-4 block text-sm text-blue-600 underline">{translateInterfaceText("Consultar assinatura pendente no checkout")}</a>}
          {rechargeError && <p role="alert" className="text-sm text-red-700 mb-4">{rechargeError}</p>}
          {view === 'recarga' && isLoadingRecharge && <p className="text-sm text-gray-600 mb-4">{translateInterfaceText("Carregando recarga automática...")}</p>}
          {rechargeConfig?.processing && <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">{translateInterfaceText("Uma recarga está em processamento. Consulte a mesma tentativa para confirmar o resultado.")}</p>
            <Button variant="outline" disabled={isResumingRecharge || isCancellingRecharge} onClick={handleResumeRecharge}>
              {isResumingRecharge ? translateInterfaceText("Consultando...") : translateInterfaceText("Consultar Recarga")}
            </Button>
          </div>}

          {view === 'comprar' && (
            <div>
              {/* Título e Descrição */}
              <h2 className="text-[20px] font-semibold text-[#111827] mb-[6px]">
                {translateInterfaceText("Adicionar Plays ao Plano")}</h2>
              <p className="text-[14px] text-[#6B7280] mb-[18px]">
                {translateInterfaceText("Compre plays extras para continuar exibindo seus vídeos")}</p>

              <SurplusQuantitySelector quantity={amount} onChange={setAmount}
                onIncrement={handleIncrement} onDecrement={handleDecrement}
                disabled={isPurchasing || purchasePending} />
              <SurplusPricingSummary quantity={amount} lockedPrice={purchasePending ? purchaseAttempt.current : null} />

              {/* Card de Pagamento */}
              <div
                className="bg-white border border-[#E5E7EB] rounded-[14px] p-4 mb-[20px]"
                style={{ boxShadow: '0 4px 10px rgba(0, 0, 0, 0.04)' }}
              >
                <p className="text-[12px] text-[#6B7280] font-medium mb-3">{translateInterfaceText("Forma de Pagamento")}</p>
                {isLoadingCards ? (
                  <div className="animate-pulse flex items-center gap-3">
                    <div className="w-10 h-6 bg-gray-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                ) : selectedCard ? (
                  <div className="flex items-center gap-3">
                    <CardBrandIcon brand={selectedCard.brand || 'unknown'} className="w-10 h-7" />
                    <div className="flex-1">
                      <p className="text-[14px] font-medium text-[#111827]">{translateInterfaceText("Final")}{" "}{selectedCard.last4}</p>
                      <p className="text-[12px] text-[#6B7280]">
                        {translateInterfaceText("Expira em")}{" "}{String(selectedCard.expiry_month).padStart(2, '0')}/{String(selectedCard.expiry_year).slice(-2)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-[12px] text-[#6B7280] hover:text-[#111827] hover:bg-[#F5F5F5]"
                      disabled={isPurchasing || purchasePending || scheduleStatus === 'scheduling' || isResumingRecharge}
                      onClick={() => setPaymentModalOpen(true)}
                    >
                      {translateInterfaceText("Alterar")}</Button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-[13px] text-[#6B7280] mb-2">{translateInterfaceText("Nenhum cartão cadastrado")}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-[12px]"
                      disabled={isPurchasing || purchasePending || scheduleStatus === 'scheduling' || isResumingRecharge}
                      onClick={() => setPaymentModalOpen(true)}
                    >
                      {translateInterfaceText("Adicionar Cartão")}</Button>
                  </div>
                )}
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isPurchasing}
                  className="flex-1 h-[44px] border-[#D1D5DB] text-[#51373a] bg-white hover:bg-[#1E4FD6] rounded-[10px] font-medium text-sm"
                >
                  {translateInterfaceText("Cancelar")}</Button>
                <Button
                  onClick={handleConfirmPurchase}
                  disabled={(!purchasePending && !quantityValid) || isPurchasing || isLoadingRecharge || !hasRecoveredPending || subscriptionPending || !rechargeConfig || rechargeConfig.processing || (!selectedCard && !purchasePending)}
                  className="flex-1 h-[44px] bg-[#2563EB] hover:bg-[#1E4FD6] active:scale-[0.97] rounded-[10px] font-semibold text-sm text-white transition-all"
                >
                  {isPurchasing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {translateInterfaceText("Processando...")}</>
                  ) : (
                    purchasePending ? translateInterfaceText("Consultar Compra") : translateInterfaceText("Confirmar Compra")
                  )}
                </Button>
              </div>
            </div>
          )}

          {view === 'recarga' && scheduleStatus !== 'scheduled' && (
            <div>
              {/* Título e Descrição */}
              <h2 className="text-[20px] font-semibold text-[#111827] mb-[6px]">
                {translateInterfaceText("Configure sua Recarga Automática")}</h2>
              <p className="text-[14px] text-[#6B7280] mb-[18px]">
                {translateInterfaceText("Ative a recarga para nunca ficar sem plays")}</p>

              <SurplusQuantitySelector quantity={amount} onChange={setAmount}
                onIncrement={handleIncrement} onDecrement={handleDecrement}
                disabled={scheduleStatus === 'scheduling' || isResumingRecharge} />
              <SurplusPricingSummary quantity={amount} />

              {/* Card de Pagamento */}
              <div
                className="bg-white border border-[#E5E7EB] rounded-[14px] p-4 mb-[20px]"
                style={{ boxShadow: '0 4px 10px rgba(0, 0, 0, 0.04)' }}
              >
                <p className="text-[12px] text-[#6B7280] font-medium mb-3">{translateInterfaceText("Forma de Pagamento")}</p>
                {isLoadingCards ? (
                  <div className="animate-pulse flex items-center gap-3">
                    <div className="w-10 h-6 bg-gray-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                ) : selectedCard ? (
                  <div className="flex items-center gap-3">
                    <CardBrandIcon brand={selectedCard.brand || 'unknown'} className="w-10 h-7" />
                    <div className="flex-1">
                      <p className="text-[14px] font-medium text-[#111827]">{translateInterfaceText("Final")}{" "}{selectedCard.last4}</p>
                      <p className="text-[12px] text-[#6B7280]">
                        {translateInterfaceText("Expira em")}{" "}{String(selectedCard.expiry_month).padStart(2, '0')}/{String(selectedCard.expiry_year).slice(-2)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-[12px] text-[#6B7280] hover:text-[#111827] hover:bg-[#F5F5F5]"
                      disabled={isPurchasing || purchasePending || scheduleStatus === 'scheduling' || isResumingRecharge}
                      onClick={() => setPaymentModalOpen(true)}
                    >
                      {translateInterfaceText("Alterar")}</Button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-[13px] text-[#6B7280] mb-2">{translateInterfaceText("Nenhum cartão cadastrado")}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-[12px]"
                      disabled={isPurchasing || purchasePending || scheduleStatus === 'scheduling' || isResumingRecharge}
                      onClick={() => setPaymentModalOpen(true)}
                    >
                      {translateInterfaceText("Adicionar Cartão")}</Button>
                  </div>
                )}
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1 h-[44px] border-[#D1D5DB] text-[#374151] bg-white hover:bg-[#F5F5F5] hover:text-[#374151] rounded-[10px] font-medium text-sm"
                >
                  {translateInterfaceText("Cancelar")}</Button>
                <Button 
                  onClick={handleScheduleRecharge}
                  disabled={!quantityValid || scheduleStatus === 'scheduling' || isLoadingRecharge || !rechargeConfig || rechargeConfig.processing || !selectedCard}
                  className="flex-1 h-[44px] bg-[#2563EB] hover:bg-[#1E4FD6] active:scale-[0.97] rounded-[10px] font-semibold text-sm text-white transition-all"
                >
                  {scheduleStatus === 'scheduling' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {translateInterfaceText("Agendando...")}</>
                  ) : (
                    translateInterfaceText("Confirmar Recarga")
                  )}
                </Button>
              </div>
            </div>
          )}

          {view === 'recarga' && scheduleStatus === 'scheduled' && (
            <div>
              {/* Check Verde Animado - PRD 2.0: 30px, mb-5 (20px) */}
              <div className="flex justify-center mb-5">
                <div className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-full bg-[#ECFDF5] animate-scale-in">
                  <Check className="w-3.5 h-3.5 text-[#059669]" />
                </div>
              </div>

              {/* Título e Descrição */}
              <h2 className="text-[20px] font-semibold text-[#111827] mb-[6px] text-center">
                {translateInterfaceText("Recarga Automática Ativada")}</h2>
              <p className="text-[14px] text-[#6B7280] mb-[18px] text-center">
                {translateInterfaceText("Você será cobrado R$")}{" "}{formatCurrency(getSurplusQuote(rechargeConfig?.quantity || amount).total_cents / 100)} {" "}{translateInterfaceText("por")}{" "}{formatNumber(rechargeConfig?.quantity || amount)} {" "}{translateInterfaceText("plays quando o saldo do plano e os plays extras acabarem")}</p>

              {/* Card de Pagamento com Badge */}
              <div
                className="bg-white border border-[#E5E7EB] rounded-[14px] p-4 mb-[20px]"
                style={{ boxShadow: '0 4px 10px rgba(0, 0, 0, 0.04)' }}
              >
                <p className="text-[12px] text-[#6B7280] font-medium mb-3">{translateInterfaceText("Forma de Pagamento")}</p>
                {selectedCard ? (
                  <div className="flex items-center gap-3">
                    <CardBrandIcon brand={selectedCard.brand || 'unknown'} className="w-10 h-7" />
                    <div className="flex-1">
                      <p className="text-[14px] font-medium text-[#111827]">{translateInterfaceText("Final")}{" "}{selectedCard.last4}</p>
                      <p className="text-[12px] text-[#6B7280]">
                        {translateInterfaceText("Expira em")}{" "}{String(selectedCard.expiry_month).padStart(2, '0')}/{String(selectedCard.expiry_year).slice(-2)}
                      </p>
                    </div>
                    {/* Badge Agendado */}
                    <div className="flex items-center gap-1.5 bg-[#ECFDF5] text-[#059669] px-[10px] py-1 rounded-full">
                      <Check className="w-3.5 h-3.5" />
                      <span className="text-[12px] font-semibold">{translateInterfaceText("Agendado")}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] text-[#6B7280]">{translateInterfaceText("Nenhum cartão selecionado")}</p>
                )}
              </div>

              {/* Botão Cancelar - PRD 2.0: font-semibold */}
              <Button 
                variant="outline"
                onClick={handleCancelSchedule}
                disabled={isCancellingRecharge || isResumingRecharge}
                className="w-full h-[44px] border-[#FECACA] text-[#EF4444] bg-white hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444] rounded-[10px] font-semibold text-sm transition-all"
              >
                {translateInterfaceText("Cancelar Agendamento")}</Button>
            </div>
          )}
        </div>
      </DialogContent>

      <PaymentMethodModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        mode="select"
        existingCards={paymentMethods}
        selectedCard={selectedCard}
        onCardSelect={(card) => {
          setSelectedCard(card);
          toast.success(translateInterfaceText("Cartão selecionado!"));
        }}
        onCardDelete={async (cardId) => {
          await deleteCard(cardId);
        }}
        onRefreshCards={async () => {
          await fetchCards();
        }}
        onSave={(data) => {
          toast.success(translateInterfaceText("Cartão adicionado com sucesso!"));
          fetchCards();
        }}
      />
    </Dialog>
  );
};

export default PlaysManagementModal;
